import type { EData, TrafficSourcesPieChartResponse } from "app/components/React/MarketingChannelsDistribution/types";
import { isHasNoWWPermissions } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { isIncludeSubDomains } from "app/UtilitiesAndConstants/UtilityFunctions/common";

export class MarketingChannelsDistributionService {
    static classInstance: MarketingChannelsDistributionService;
    private observers = [];
    private data;
    private readonly isWWOApiController: boolean;
    private readonly queryParams: { [key: string]: string };

    constructor(queryParams) {
        if (MarketingChannelsDistributionService.classInstance && !this.isQueryParamsChanged(queryParams)) {
            return MarketingChannelsDistributionService.classInstance;
        }

        MarketingChannelsDistributionService.classInstance = this;
        const { country } = queryParams;
        let { duration } = queryParams;
        this.isWWOApiController = isHasNoWWPermissions(country);
        if (this.isWWOApiController) {
            duration = BasicDurations.LAST_THREE_MONTHS;
        }
        this.queryParams = { ...queryParams, duration };
        this.fetchDataAndNotifyAllObservers();
    }

    private fetchDataAndNotifyAllObservers = async () => {
        await this.fetchData();
        this.notifyAllObservers();
    };

    private fetchData = async () => {
        const { key, isWWW, duration } = this.queryParams;
        const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
        const ctrl = this.isWWOApiController
            ? "WorldwideOverviewTotal/TrafficSourcesTotalOverview"
            : "MarketingMixTotal/TrafficSourcesOverview";
        const timeGranularity = duration === "28d" ? "Daily" : "Monthly";

        const queryParams = {
            country: this.queryParams.country,
            keys: key,
            from,
            to,
            isWindow,
            timeGranularity,
            includeSubDomains: isIncludeSubDomains(isWWW),
        };
        try {
            const response = await DefaultFetchService.getInstance().get<TrafficSourcesPieChartResponse>(
                `widgetApi/${ctrl}/PieChart`,
                queryParams,
            );
            this.data = { Data: response.Data?.Total ?? response.Data };
        } catch (_e) {
            this.data = null;
        }
    };

    private notifyAllObservers = () => {
        this.observers.map(this.invokeObserverResolve);
        this.removeAllObservers();
    };

    private invokeObserverResolve = (observerResolve) => {
        observerResolve(this.data);
    };

    private removeAllObservers = () => {
        this.observers = [];
    };

    private isQueryParamsChanged = (queryParams) => {
        const { duration, country, key, isWWW } = queryParams;
        const { queryParams: thisQueryParams } = MarketingChannelsDistributionService.classInstance;
        const { duration: thisDuration, country: thisCountry, key: thisKey, isWWW: thisIsWWW } = thisQueryParams;
        return duration !== thisDuration || thisCountry !== country || key !== thisKey || isWWW !== thisIsWWW;
    };

    public getData = async () =>
        new Promise<EData>((resolve) => (this.data ? resolve(this.data) : this.observers.push(resolve)));
}
